import React, { useState, useEffect } from 'react';

const FlashCard = ({ word, onSwipe, isAnimating, onDragUpdate, showEnglishFirst, exitDirection }) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [dragDistance, setDragDistance] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [localExitDirection, setLocalExitDirection] = useState(null);
  const [isEntering, setIsEntering] = useState(false);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const [isJiggling, setIsJiggling] = useState(false);

  // Minimum swipe distance for triggering action
  const minSwipeDistance = 50;

  useEffect(() => {
    // Reset flip state when word changes - always start unflipped
    setIsFlipped(false);
    setDragDistance(0);
    setIsDragging(false);
    setLocalExitDirection(null);
    
    // Trigger entrance animation for new card (but not on first render)
    if (!isFirstRender) {
      setIsEntering(true);
      const timer = setTimeout(() => {
        setIsEntering(false);
      }, 250); // Animation duration
      
      return () => clearTimeout(timer);
    } else {
      setIsFirstRender(false);
    }
  }, [word, showEnglishFirst]);

  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    setIsDragging(true);
    onDragUpdate && onDragUpdate(true, 0);
  };

  const handleTouchMove = (e) => {
    if (!touchStart) return;
    
    const currentTouch = e.targetTouches[0].clientX;
    const distance = currentTouch - touchStart;
    setDragDistance(distance);
    setTouchEnd(currentTouch);
    onDragUpdate && onDragUpdate(true, distance);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) {
      setIsDragging(false);
      setDragDistance(0);
      setTouchStart(null);
      setTouchEnd(null);
      onDragUpdate && onDragUpdate(false, 0);
      return;
    }
    
    const distance = touchEnd - touchStart;
    const isLeftSwipe = distance < -minSwipeDistance;
    const isRightSwipe = distance > minSwipeDistance;
    
    if (isLeftSwipe) {
      setLocalExitDirection('left');
      onSwipe('dont-know');
    } else if (isRightSwipe) {
      setLocalExitDirection('right');
      onSwipe('know');
    }
    
    // Only reset states if no swipe occurred
    if (!isLeftSwipe && !isRightSwipe) {
      setIsDragging(false);
      setDragDistance(0);
      onDragUpdate && onDragUpdate(false, 0);
    }
    
    setTouchStart(null);
    setTouchEnd(null);
  };

  const handleMouseDown = (e) => {
    setTouchStart(e.clientX);
    setIsDragging(true);
    onDragUpdate && onDragUpdate(true, 0);
  };

  const handleMouseMove = (e) => {
    if (!touchStart || !isDragging) return;
    
    const distance = e.clientX - touchStart;
    setDragDistance(distance);
    onDragUpdate && onDragUpdate(true, distance);
  };

  const handleMouseUp = (e) => {
    if (!touchStart) {
      setIsDragging(false);
      setDragDistance(0);
      onDragUpdate && onDragUpdate(false, 0);
      return;
    }
    
    const distance = e.clientX - touchStart;
    const isLeftSwipe = distance < -minSwipeDistance;
    const isRightSwipe = distance > minSwipeDistance;
    
    if (isLeftSwipe) {
      setLocalExitDirection('left');
      onSwipe('dont-know');
    } else if (isRightSwipe) {
      setLocalExitDirection('right');
      onSwipe('know');
    }
    
    // Only reset states if no swipe occurred
    if (!isLeftSwipe && !isRightSwipe) {
      setIsDragging(false);
      setDragDistance(0);
      onDragUpdate && onDragUpdate(false, 0);
    }
    
    setTouchStart(null);
  };

  const handleClick = (e) => {
    // Only flip if not dragging significantly and not animating
    if (Math.abs(dragDistance) < 10 && !isAnimating) {
      setIsFlipped(!isFlipped);
      // Trigger jiggle animation
      setIsJiggling(true);
      setTimeout(() => setIsJiggling(false), 350);
    }
  };

  const getCardStyle = () => {
    // If entering, scale from 0 to 1
    if (isEntering) {
      return {
        transform: 'scale(0)',
        animation: 'cardEnter 0.25s cubic-bezier(0.34, 1.56, 0.64, 1) forwards'
      };
    }
    
    // If jiggling (after flip), add jiggle animation
    if (isJiggling && !isDragging) {
      return {
        animation: 'cardJiggle 0.35s cubic-bezier(0.68, -0.55, 0.265, 1.55)'
      };
    }
    
    // If exiting, animate out with slide and shrink
    const currentExitDirection = exitDirection || localExitDirection;
    if (currentExitDirection) {
      return {
        animation: `cardExit${currentExitDirection === 'left' ? 'Left' : 'Right'} 0.3s ease-out forwards`
      };
    }
    
    // Normal animation check
    if (isAnimating) return {};
    
    // Only apply drag styles if dragging significantly (more than 5px)
    if (isDragging && Math.abs(dragDistance) > 5) {
      const rotation = dragDistance * 0.1;
      const opacity = Math.max(0.7, 1 - Math.abs(dragDistance) * 0.002);
      return {
        transform: `translateX(${dragDistance}px) rotate(${rotation}deg)`,
        opacity: opacity,
        transition: 'none'
      };
    }
    
    return {};
  };

  const getSwipeClass = () => {
    if (!isDragging) return '';
    if (dragDistance > minSwipeDistance) return 'swipe-right';
    if (dragDistance < -minSwipeDistance) return 'swipe-left';
    return '';
  };

  // Example sentence in the language of the visible side.
  // `example` is either { swedish, english } or a plain Swedish string.
  const isEnglishSide = (isFlipped && !showEnglishFirst) || (!isFlipped && showEnglishFirst);
  const example = word ? word.example : undefined;
  const exampleSentence = example && typeof example === 'object'
    ? (isEnglishSide ? example.english : example.swedish)
    : (!isEnglishSide && typeof example === 'string' ? example : '');

  return (
    <div className={`card-container${example ? ' has-example' : ''}`}>
      <div
        className={`flash-card ${getSwipeClass()}`}
        style={getCardStyle()}
        onClick={handleClick}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={() => {
          // Only reset if not in exit animation
          if (!exitDirection && !localExitDirection) {
            setIsDragging(false);
            setDragDistance(0);
            setTouchStart(null);
            onDragUpdate && onDragUpdate(false, 0);
          }
        }}
      >
        <div className={`card-face card-front ${
          (isFlipped && !showEnglishFirst) || (!isFlipped && showEnglishFirst) 
            ? 'english-side' 
            : 'swedish-side'
        }`}>
          <div className="word-type">{word.type}</div>
          <div className="word-main">
            {(isFlipped && !showEnglishFirst) || (!isFlipped && showEnglishFirst) 
              ? word.english 
              : (typeof word.swedish === 'object' && word.swedish !== null
                  ? (word.swedish.infinitive || '')
                  : word.swedish)}
          </div>
          {((!isFlipped && !showEnglishFirst) || (isFlipped && showEnglishFirst)) && (
            <>
              {word.pronunciation && (
                <div className="pronunciation">/{String(word.pronunciation).replace(/^\/+|\/+$/g, '')}/</div>
              )}
              {word.article && (
                <div className="pronunciation">Article: {word.article}</div>
              )}
              {typeof word.swedish === 'object' && word.swedish !== null && (
                <div className="verb-forms">
                  {word.swedish.imperative && (
                    <>
                      <span className="verb-label">Imperative:</span>
                      <span className="verb-value">{word.swedish.imperative}</span>
                    </>
                  )}
                  {word.swedish.present && (
                    <>
                      <span className="verb-label">Present:</span>
                      <span className="verb-value">{word.swedish.present}</span>
                    </>
                  )}
                  {word.swedish.preteritum && (
                    <>
                      <span className="verb-label">Preteritum:</span>
                      <span className="verb-value">{word.swedish.preteritum}</span>
                    </>
                  )}
                  {word.swedish.supinum && (
                    <>
                      <span className="verb-label">Supinum:</span>
                      <span className="verb-value">{word.swedish.supinum}</span>
                    </>
                  )}
                </div>
              )}
              {word.plural && (
                <div className="pronunciation">Plural: {word.plural}</div>
              )}
              {word.preteritum !== undefined && word.preteritum !== null && String(word.preteritum).length > 0 && (
                <div className="pronunciation">Preteritum: {word.preteritum}</div>
              )}
            </>
          )}
          {exampleSentence && (
            <div className="example-sentence">“{exampleSentence}”</div>
          )}
          <div className="flip-hint">👆 Click to flip</div>
        </div>
      </div>
    </div>
  );
};

export default FlashCard;